import { PARAMETER_NAMES, TIER_PARAMETERS, TIER_INFO, getGrade, formatCurrency } from "./DashboardShared";

// Helper to get display title from action item
const getActionTitle = (action) => {
  // If title contains a camelCase parameter key, use PARAMETER_NAMES
  if (action.title) {
    // Check if title is a raw parameter key
    if (PARAMETER_NAMES[action.title]) {
      return PARAMETER_NAMES[action.title];
    }
    // Check if title starts with "Improve " followed by a parameter key
    const improveMatch = action.title.match(/^Improve\s+(.+)$/i);
    if (improveMatch) {
      const paramKey = improveMatch[1];
      if (PARAMETER_NAMES[paramKey]) {
        return PARAMETER_NAMES[paramKey];
      }
    }
  }
  // Fall back to parameter name if available
  if (action.parameter && PARAMETER_NAMES[action.parameter]) {
    return PARAMETER_NAMES[action.parameter];
  }
  // Last resort: return original title or generic
  return action.title || `Action Item`;
};

const ActionPlanTab = ({ actionPlan, data }) => {
  const actions = actionPlan || [];
  const firstAction = actions[0];
  const lockedActions = actions.slice(1);
  const financialMetrics = data?.financialMetrics || data?.metrics || {};
  const totalRecoverable = financialMetrics.totalRecoverableRevenue || 0;
  const currency = data?.input?.currency || "USD";

  const getRecovery = (action) => {
    if (!totalRecoverable || !action?.weight) return null;
    const val = totalRecoverable * (action.weight / 100);
    return val > 0 ? val : null;
  };

  const getTierName = (param) => {
    for (const [tier, params] of Object.entries(TIER_PARAMETERS)) {
      if (params.includes(param)) return TIER_INFO[tier]?.name || "Priority";
    }
    return "Priority";
  };

  if (!firstAction) return (
    <div style={{ padding: 24, textAlign: "center", color: "rgba(10,10,26,0.4)", fontSize: 13 }}>
      Action plan not available for this audit.
    </div>
  );

  const recovery = getRecovery(firstAction);
  const observation = firstAction.observation || firstAction.observations?.[0] || firstAction.recommendation || "";
  const findings = firstAction.findings || [];

  const getPriorityBand = (action) => {
    const score = action?.currentScore ?? action?.score ?? 5;
    if (score <= 3) return "high";
    if (score <= 6) return "medium";
    return "low";
  };
  const highCount = actions.filter(a => getPriorityBand(a) === "high").length;
  const medCount = actions.filter(a => getPriorityBand(a) === "medium").length;
  const lowCount = actions.filter(a => getPriorityBand(a) === "low").length;
  const top2Weight = actions.slice(0, 2).reduce((sum, a) => sum + (a.weight || 0), 0);

  return (
    <div>
      <div className="print-section-title">Action Plan</div>
      <div style={{ background: "#FFFFFF", border: "1px solid rgba(10,10,26,0.08)", borderLeft: "3px solid #2D4DE0", borderRadius: 14, padding: "20px 24px", marginBottom: 14 }}>
        <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 17, fontWeight: 800, color: "#0A0A1A", marginBottom: 4 }}>Your Prioritised Action Plan</div>
        <div style={{ fontSize: 12, color: "rgba(10,10,26,0.4)", marginBottom: 16, lineHeight: 1.5 }}>
          {actions.length} actions ranked by revenue recovery potential — fixing the top 2 alone addresses over {top2Weight}% of your total weighted score.
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          {[{ label: "High Priority", count: highCount, bg: "#F0F1F7", color: "#0A0F2E" },
            { label: "Medium Priority", count: medCount, bg: "#F0F1F7", color: "#0A0F2E" },
            { label: "Low Priority", count: lowCount, bg: "rgba(10,10,26,0.04)", color: "rgba(10,10,26,0.4)" }
          ].map(({ label, count, bg, color }) => (
            <div key={label} style={{ flex: 1, background: bg, borderRadius: 10, padding: "12px 16px", textAlign: "center" }}>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 22, fontWeight: 800, color }}>{count}</div>
              <div style={{ fontSize: 11, color: "rgba(10,10,26,0.5)", marginTop: 2 }}>{label}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ background: "#FFFFFF", border: "1px solid rgba(10,10,26,0.08)", borderLeft: "3px solid #2D4DE0", borderRadius: 14, padding: 28, marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <div style={{ width: 26, height: 26, borderRadius: "50%", background: "#2D4DE0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, fontWeight: 700, color: "#FFFFFF" }}>1</span>
          </div>
          <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 15, fontWeight: 700, color: "#0A0A1A" }}>{firstAction.title || PARAMETER_NAMES[firstAction.parameter] || "Top Priority"}</span>
        </div>
        <div style={{ display: "flex", gap: 6, marginBottom: 16, marginLeft: 36 }}>
          <span style={{ fontSize: 9.5, fontWeight: 600, borderRadius: 4, padding: "2px 8px", background: "rgba(45,77,224,0.08)", color: "#2D4DE0" }}>{getTierName(firstAction.parameter)}</span>
          <span style={{ fontSize: 9.5, fontWeight: 600, borderRadius: 4, padding: "2px 8px", background: "#F0F1F7", color: "#0A0F2E" }}>High Impact</span>
        </div>
        {observation ? (
          <div style={{ fontSize: 13, color: "#0A0A1A", lineHeight: 1.8, marginLeft: 36, marginBottom: 20 }}>{observation}</div>
        ) : null}
        <div style={{ height: 1, background: "rgba(10,10,26,0.06)", margin: "0 0 20px 36px" }} />
        {recovery && (
          <div style={{ marginLeft: 36, marginBottom: findings.length > 0 ? 20 : 0, padding: "14px 18px", background: "rgba(15,42,138,0.04)", border: "1px solid rgba(15,42,138,0.08)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "rgba(10,10,26,0.3)", marginBottom: 4 }}>Revenue Recovery Potential</div>
              <div style={{ fontSize: 12, color: "rgba(10,10,26,0.55)", lineHeight: 1.5 }}>Estimated annual direct revenue recoverable by closing this gap.</div>
            </div>
            <div style={{ flexShrink: 0, textAlign: "right" }}>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 28, fontWeight: 800, color: "#0F2A8A", lineHeight: 1, letterSpacing: -1 }}>{formatCurrency(recovery, currency)}</div>
              <div style={{ fontSize: 10, color: "rgba(10,10,26,0.3)", marginTop: 3 }}>Annual estimate</div>
            </div>
          </div>
        )}
        {findings.length > 0 && (
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "rgba(10,10,26,0.3)", marginBottom: 10, marginLeft: 36 }}>Critical Findings</div>
            {findings.map((finding, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginLeft: 36, padding: "8px 0", borderBottom: i < findings.length - 1 ? "1px solid rgba(10,10,26,0.04)" : "none" }}>
                <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#2D4DE0", flexShrink: 0 }} />
                <span style={{ fontSize: 12.5, fontWeight: 600, color: "#0A0A1A", flex: 1 }}>{finding}</span>
                <span style={{ fontSize: 9, fontWeight: 600, borderRadius: 4, padding: "2px 7px", background: "#F0F1F7", color: "#0A0F2E", flexShrink: 0 }}>Critical</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={{ position: "relative" }}>
        {lockedActions.map((action, i) => (
          <div key={i} style={{ opacity: 0.45, filter: "blur(1.5px)", pointerEvents: "none", borderRadius: 12, padding: "14px 20px", marginBottom: 6, background: "#FFFFFF", border: "1px solid rgba(10,10,26,0.06)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(10,10,26,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 700, color: "rgba(10,10,26,0.3)" }}>{i + 2}</span>
              </div>
              <span style={{ fontSize: 13, fontWeight: 600, color: "rgba(10,10,26,0.4)" }}>{action.title || PARAMETER_NAMES[action.parameter] || `Action ${i + 2}`}</span>
            </div>
          </div>
        ))}
        <div style={{ position: "absolute", top: 60, left: "50%", transform: "translateX(-50%)", background: "#FFFFFF", borderRadius: 14, padding: "24px 28px", boxShadow: "0 8px 32px rgba(0,0,0,0.12)", textAlign: "center", width: 320, zIndex: 10 }}>
          <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 16, fontWeight: 700, color: "#0A0A1A", marginBottom: 8 }}>Unlock Your Full Action Plan</div>
          <div style={{ fontSize: 12.5, color: "rgba(10,10,26,0.5)", marginBottom: 18, lineHeight: 1.5 }}>Book a free 30-min call with a dhi Hospitality expert to get your complete roadmap.</div>
          <button
            onClick={() => window.open('https://calendly.com/dhihospitality-audit-review/30min?hide_event_type_details=1&hide_gdpr_banner=1', '_blank')}
            style={{ display: "block", width: "100%", background: "#2D4DE0", color: "#FFFFFF", padding: "12px 28px", borderRadius: 8, fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer" }}
          >
            Book a Free Strategy Call
          </button>
        </div>
      </div>
      <div style={{ marginTop: 24 }}>
        <div style={{ background: "#0A0A1A", borderRadius: 14, padding: 24 }}>
          <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", color: "rgba(255,255,255,0.4)", marginBottom: 8 }}>Next Step</div>
          <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 17, fontWeight: 700, color: "#FFFFFF", marginBottom: 6 }}>Turn findings into a direct booking strategy.</div>
          <div style={{ fontSize: 12.5, color: "rgba(255,255,255,0.5)", marginBottom: 16, lineHeight: 1.5 }}>A dhi strategist will walk you through your results and tell you exactly what to fix first.</div>
          <button
            onClick={() => window.open('https://calendly.com/dhihospitality-audit-review/30min?hide_event_type_details=1&hide_gdpr_banner=1', '_blank')}
            style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#2D4DE0", border: "none", borderRadius: 8, padding: "10px 20px", cursor: "pointer" }}
          >
            <span style={{ fontSize: 13, fontWeight: 600, color: "#FFFFFF" }}>Book a Strategy Call</span>
            <span style={{ color: "#FFFFFF" }}>→</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActionPlanTab;
